import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { api } from '../api';

const ease = [0.22, 1, 0.36, 1];

const DEFAULT_CONFIG = {
  eyebrow: 'Technology Consulting & AI Studio',
  cta: 'Start a Project',
};

const HERO_SLIDES = [
  {
    heading: ['Build Smarter.', 'Scale Faster.', 'With AI.'],
    accent: 2,
    tagline: 'We help startups, SMEs, and enterprises design, build, and scale digital products — powered by engineering, design, and artificial intelligence.',
  },
  {
    heading: ['Turn Ideas Into', 'Products.', 'Fast.'],
    accent: 1,
    tagline: 'From concept to production in weeks — not months. We move with velocity without breaking what matters.',
  },
  {
    heading: ['AI That Works.', 'Not Hype.', 'Real ROI.'],
    accent: 1,
    tagline: 'Practical AI adoption that delivers measurable results. No endless pilots. No vague strategy. Just working software.',
  },
  {
    heading: ['Your Technology', 'Partner.', 'End to End.'],
    accent: 1,
    tagline: 'Senior engineers, designers, and AI experts embedded in your team — from the first line of strategy to the last line of code.',
  },
  {
    heading: ['Invisible Tech.', 'Visible', 'Business Impact.'],
    accent: 1,
    tagline: 'Great software feels effortless. We obsess over the unseen details that create products people love without knowing why.',
  },
];

const BADGES = [
  { icon: CheckCircle, text: '200+ Products Shipped' },
  { icon: CheckCircle, text: 'AI-Native Approach' },
  { icon: CheckCircle, text: '15+ Industries' },
];

const STATEMENTS = [
  { tag: 'Our Vision',   text: 'Technology should feel like it was made for you — not adapted from a template.', color: '#635BFF' },
  { tag: 'What We Do',  text: 'We design, build, and scale digital products powered by engineering, design, and AI.', color: '#0073E6' },
  { tag: 'Why We Do It',text: 'Because the best technology is invisible — it just works, and it accelerates everything.', color: '#00D4AA' },
];

// Mini product preview cards shown below CTA (like Stripe's product preview)
function PreviewCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease }}
      className="flex items-center gap-3 px-4 py-3 rounded-2xl"
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}
    >
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}12` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div>
        <div className="text-[11px] font-medium" style={{ color: '#9CA3AF' }}>{label}</div>
        <div className="text-[13px] font-bold" style={{ color: '#0A0527' }}>{value}</div>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const shouldReduce = useReducedMotion();
  const [cfg, setCfg]             = useState(DEFAULT_CONFIG);
  const [stats, setStats]         = useState({ brands: 300, products: 200, ventures: 100 });
  const [activeIdx, setActiveIdx] = useState(0);
  const [slideIdx, setSlideIdx]   = useState(0);

  useEffect(() => {
    api.getHero().then(d => { if (d?.eyebrow || d?.cta) setCfg(prev => ({ ...prev, ...d })); }).catch(() => {});
    api.getStats().then(d => { if (d?.brands) setStats(d); }).catch(() => {});
  }, []);

  // Auto-rotate main slides every 4s
  useEffect(() => {
    const t = setInterval(() => setSlideIdx(i => (i + 1) % HERO_SLIDES.length), 4000);
    return () => clearInterval(t);
  }, []);

  // Auto-rotate bottom statements every 3.5s
  useEffect(() => {
    const t = setInterval(() => setActiveIdx(i => (i + 1) % STATEMENTS.length), 3500);
    return () => clearInterval(t);
  }, []);

  const slide = HERO_SLIDES[slideIdx];


  return (
    <section id="hero" className="relative overflow-hidden stripe-mesh" style={{ minHeight: '100dvh' }}>
      <div className="relative flex flex-col min-h-[100dvh]" style={{ zIndex: 2 }}>
        <div className="h-24" /> {/* nav spacer */}

        {/* ── Center-aligned hero content (Stripe style) ── */}
        <div className="flex-1 flex flex-col items-center justify-center px-5 sm:px-8 text-center">
          <div className="max-w-4xl w-full">

            {/* Eyebrow */}
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.45, ease }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-semibold mb-8"
              style={{
                background: 'rgba(99,91,255,0.08)',
                border: '1px solid rgba(99,91,255,0.2)',
                color: '#635BFF',
              }}
            >
              <Sparkles size={12} />
              {cfg.eyebrow}
            </motion.span>

            {/* Auto-cycling heading */}
            <div className="mb-6 min-h-[14rem] sm:min-h-[16rem] flex flex-col justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={slideIdx}
                  initial={{ opacity: 0, y: shouldReduce ? 0 : 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: shouldReduce ? 0 : -30 }}
                  transition={{ duration: 0.5, ease }}
                >
                  {slide.heading.map((line, i) => (
                    <span
                      key={i}
                      className={`block font-black leading-tight ${i === slide.accent ? 'grad-text' : ''}`}
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 'clamp(2.8rem, 5.5vw, 5.5rem)',
                        letterSpacing: '-0.035em',
                        color: i === slide.accent ? undefined : '#0A0527',
                      }}
                    >
                      {line}
                    </span>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Auto-cycling tagline */}
            <div className="mb-10 min-h-[4rem] flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={slideIdx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45, delay: 0.1, ease }}
                  className="text-lg sm:text-xl leading-relaxed max-w-2xl"
                  style={{ color: '#6B7280' }}
                >
                  {slide.tagline}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Slide progress dots */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSlideIdx(i)}
                  className="rounded-full transition-all duration-400"
                  style={{
                    width: slideIdx === i ? 24 : 6,
                    height: 6,
                    background: slideIdx === i ? '#635BFF' : 'rgba(0,0,0,0.12)',
                  }}
                />
              ))}
            </div>

            {/* Auto-rotating statements */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.48, duration: 0.45, ease }}
              className="mb-8 flex flex-col items-center gap-3"
            >
              {/* Dot indicators */}
              <div className="flex items-center gap-2">
                {STATEMENTS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: activeIdx === i ? 20 : 6,
                      height: 6,
                      background: activeIdx === i ? s.color : 'rgba(0,0,0,0.12)',
                    }}
                  />
                ))}
              </div>

              {/* Statement card */}
              <div className="relative h-20 w-full max-w-2xl overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIdx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.38, ease }}
                    className="absolute inset-0 flex items-center justify-center px-4"
                  >
                    <div
                      className="flex items-start gap-3 px-5 py-3.5 rounded-2xl w-full"
                      style={{
                        background: `${STATEMENTS[activeIdx].color}09`,
                        border: `1px solid ${STATEMENTS[activeIdx].color}25`,
                      }}
                    >
                      <span
                        className="shrink-0 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full mt-0.5"
                        style={{ background: `${STATEMENTS[activeIdx].color}15`, color: STATEMENTS[activeIdx].color }}
                      >
                        {STATEMENTS[activeIdx].tag}
                      </span>
                      <p className="text-sm font-medium leading-snug text-left" style={{ color: '#374151' }}>
                        {STATEMENTS[activeIdx].text}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>

            {/* CTA buttons */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.45, ease }}
              className="flex flex-wrap items-center justify-center gap-4 mb-10"
            >
              <motion.a href="#contact" whileTap={{ scale: 0.97 }} className="btn-primary">
                {cfg.cta || 'Start a Project'} <ArrowRight size={16} />
              </motion.a>
              <motion.a href="#studios" whileTap={{ scale: 0.97 }} className="btn-secondary">
                See Case Studies
              </motion.a>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="flex flex-wrap items-center justify-center gap-5"
            >
              {BADGES.map(({ icon: Icon, text }) => (
                <span key={text} className="flex items-center gap-1.5 text-[13px] font-medium" style={{ color: '#9CA3AF' }}>
                  <Icon size={14} style={{ color: '#635BFF' }} /> {text}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* ── Stats bar ─────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5, ease }}
          className="px-5 sm:px-8 md:px-12 lg:px-20 py-8"
          style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-3 gap-6 sm:gap-12 text-center">
              {[
                { val: `${stats.brands}+`, label: 'Crafted Brands' },
                { val: `${stats.products}+`, label: 'Digital Products' },
                { val: `${stats.ventures}+`, label: 'Ventures Funded' },
              ].map(s => (
                <div key={s.label}>
                  <div
                    className="font-black grad-text leading-none"
                    style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
                      letterSpacing: '-0.03em',
                    }}
                  >
                    {s.val}
                  </div>
                  <div className="text-sm font-medium mt-1" style={{ color: '#9CA3AF' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
